import { C_folderBrand } from 'types/yext'

/**
 * Represents the types of marker icons for stores.
 * @typedef {('pinGeneric' | 'pinGA' | 'pinEA' | 'pinAX' | 'pinCasa')} MarkersIcons
 */
type MarkersIcons = 'pinGeneric' | 'pinGA' | 'pinEA' | 'pinAX' | 'pinCasa'

/**
 * Extends SearchApiResult with additional store map properties.
 * @interface StoreMap
 * @property {`/images/map/${MarkersIcons}.svg`} icon - The icon path for the store marker.
 * @property {boolean} visible - Whether the store is currently visible on the map.
 * @property {number} distance - The distance of the store from a reference point.
 */
export interface StoreMap extends SearchApiResult {
  icon: `/images/map/${MarkersIcons}.svg`
  visible: boolean
  distance: number
}

/**
 * Composable for managing store locator functionality.
 * Provides methods and state for finding, filtering, and displaying stores.
 *
 * @returns {Object} Store locator utilities and state
 * @property {Function} checkExistingStoreOrBoutique - Fetch stores or boutiques based on filter
 * @property {Ref<Address>} selectedStore - Currently selected store
 * @property {Ref<YextAddress>} selectedStoreDetails - Details of the selected store
 * @property {Ref<StoresVerticalResults | null>} stores - List of stores
 * @property {Ref<Coordinates | undefined>} centerMap - Center coordinates of the map
 * @property {Function} setCenterMap - Set the center map coordinates
 * @property {Function} findStoresByLocation - Find stores near specific coordinates
 * @property {Function} setStoreVisible - Toggle store visibility on map
 * @property {Function} getIcon - Get map marker icon for a specific store type
 */
export const useStoreLocator = () => {
  const { country, language } = useRouteHelper()
  const { cartItemsByBrand } = useCartUtilities()

  /** @type {Ref<Address>} Currently selected store */
  const selectedStore = useState<Address>()

  /** @type {Ref<YextAddress>} Details of the selected store */
  const selectedStoreDetails = useState<YextAddress>()

  /** @type {Ref<StoresVerticalResults | null>} List of stores */
  const stores = useState<StoresVerticalResults | null>(
    `stores-map`,
    () => null
  )

  /** @type {Object} Predefined filter flags for stores and boutiques */
  const filterFlags = {
    store: 'c_pickupInStore=true',
    boutique: 'c_isAGoogleBoutique=true',
  }

  /**
   * Check for existing stores or boutiques based on optional filter.
   *
   * @param {Object} [options] - Options for filtering stores
   * @param {'store' | 'boutique' | false} [options.filterFor=false] - Type of filter to apply
   * @returns {Promise<any>} Fetched store or boutique data
   * @throws {Error} If there's an issue fetching store data
   */
  const checkExistingStoreOrBoutique = async (
    {
      filterFor,
    }: {
      filterFor: 'store' | 'boutique' | false
    } = { filterFor: false }
  ) => {
    try {
      const filter = filterFor
        ? filterFlags[filterFor as keyof typeof filterFlags]
        : ''
      return await $fetch('/api/yext/getStoresAndBoutiques', {
        method: 'POST',
        body: { country, filter },
      })
    } catch (error) {
      console.error('Error fetching store data:', error)
    }
  }

  /** @type {Ref<Coordinates | undefined>} Center coordinates of the map */
  const centerMap = useState<Coordinates | undefined>(
    'centerMap',
    () => undefined
  )

  /**
   * Set the center map coordinates.
   *
   * @param {Coordinates} coords - Coordinates to set as map center
   */
  const setCenterMap = (coords: Coordinates) => {
    centerMap.value = coords
  }

  /**
   * Compute brands from cart items.
   * @type {ComputedRef<Array<Object>>} List of brand filters
   */
  const brands = computed(() => {
    const keys = cartItemsByBrand.value.keys()
    return Array.from(keys).map((el: string) => {
      const key = el.replace('-', '_').toUpperCase()
      return {
        c_folderBrand: {
          $eq: C_folderBrand[key as keyof typeof C_folderBrand],
        },
      }
    })
  })

  /**
   * Find stores by location coordinates.
   *
   * @param {Object} options - Search options
   * @param {Coordinates} [options.coords] - Coordinates to search near
   * @returns {Promise<StoresVerticalResults>} Stores found near the coordinates
   */
  const findStoresByLocation = async ({ coords }: { coords?: Coordinates }) =>
    await $fetch<StoresVerticalResults>('/api/yext/getStoresByLocation', {
      method: 'POST',
      query: {
        location: `${coords?.lat},${coords?.lng}`,
        language,
      },
      body: {
        $or: [...brands.value],
        $and: [{ 'address.countryCode': { $eq: country.toUpperCase() } }],
      },
    })

  /**
   * Set the visibility of a specific store.
   *
   * @param {string} idStore - ID of the store to modify
   * @param {boolean} visible - Visibility state to set
   */
  const setStoreVisible = (idStore: string, visible: boolean) => {
    if (stores.value && stores.value.results.length) {
      stores.value = {
        ...stores.value,
        results:
          stores.value?.results?.map(store =>
            store.data.id === idStore
              ? {
                  ...store,
                  visible: visible,
                }
              : store
          ) ?? [],
      }
    }
  }

  /**
   * Get the appropriate map marker icon for a store type.
   *
   * @param {C_folderBrand} [storeType] - Type of store brand
   * @returns {string} Path to the store's map marker icon
   */
  const getIcon = (storeType?: C_folderBrand | null) => {
    const defaultIcon = '/images/map/pinGeneric.svg'
    if (!storeType) return defaultIcon
    const icons: Partial<Record<C_folderBrand, StoreMap['icon']>> = {
      'Giorgio Armani': '/images/map/pinGA.svg',
      'Emporio Armani': '/images/map/pinEA.svg',
      'Emporio Armani Junior': '/images/map/pinEA.svg',
      'Armani Exchange': '/images/map/pinAX.svg',
      'Armani/Casa': '/images/map/pinCasa.svg',
    }
    return icons[storeType] || defaultIcon
  }

  return {
    checkExistingStoreOrBoutique,
    selectedStore,
    selectedStoreDetails,
    stores,
    centerMap,
    setCenterMap,
    findStoresByLocation,
    setStoreVisible,
    getIcon,
  }
}
